import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ptBR } from "date-fns/locale";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clienteService, obrigacaoService } from "../services/api";

const ObrigacoesEnviadas = () => {
  const navigate = useNavigate();

  const [obrigacoes, setObrigacoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    aberto: false,
    mensagem: "",
    tipo: "success",
  });

  const [filtros, setFiltros] = useState({
    dataInicio: null,
    dataFim: null,
    cliente: "",
  });

  const [paginacao, setPaginacao] = useState({
    page: 0,
    limit: 10,
    total: 0,
  });

  const mostrarSnackbar = (mensagem, tipo = "success") => {
    setSnackbar({ aberto: true, mensagem, tipo });
  };

  const fecharSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, aberto: false }));
  };

  const carregarClientes = useCallback(async () => {
    try {
      const response = await clienteService.getAll({ limit: 1000 });
      setClientes(response.clientes || []);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
    }
  }, []);

  const carregarRelatorio = async () => {
    setLoading(true);
    try {
      const params = {
        page: paginacao.page + 1,
        limit: paginacao.limit,
        ...(filtros.dataInicio && {
          data_inicio: filtros.dataInicio.toISOString().split("T")[0],
        }),
        ...(filtros.dataFim && {
          data_fim: filtros.dataFim.toISOString().split("T")[0],
        }),
        ...(filtros.cliente && { cliente_id: filtros.cliente }),
      };

      const response =
        await obrigacaoService.getRelatorioObrigacoesEnviadas(params);
      setObrigacoes(response.obrigacoes || []);
      setPaginacao((prev) => ({
        ...prev,
        total: response.pagination?.total || 0,
      }));
    } catch (error) {
      mostrarSnackbar("Erro ao carregar relatório: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarClientes();
  }, [carregarClientes]);

  // Removido useEffect que carregava automaticamente o relatório

  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const handleChangePage = (event, newPage) => {
    setPaginacao((prev) => ({ ...prev, page: newPage }));
    // Só recarrega se já tiver dados carregados
    if (obrigacoes.length > 0) {
      carregarRelatorio();
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setPaginacao((prev) => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 0,
    }));
    // Só recarrega se já tiver dados carregados
    if (obrigacoes.length > 0) {
      carregarRelatorio();
    }
  };

  const handleGerarRelatorio = () => {
    setPaginacao((prev) => ({ ...prev, page: 0 }));
    carregarRelatorio();
  };

  const handleVoltar = () => {
    navigate("/relatorios");
  };

  const formatarData = (data) => {
    if (!data) return "-";
    // Usar formato de string diretamente para evitar problemas de timezone
    const [ano, mes, dia] = data.split("T")[0].split("-");
    return `${dia}/${mes}/${ano}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "enviado":
        return "success";
      case "enviado_atrasado":
        return "warning";
      case "pago":
        return "info";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "enviado":
        return "Enviado";
      case "enviado_atrasado":
        return "Enviado atrasado";
      case "pago":
        return "Pago";
      default:
        return status;
    }
  };

  const calcularPrazoLegal = (tipoObrigacao) => {
    if (!tipoObrigacao) return "-";

    if (tipoObrigacao.mes_recorrente) {
      return `${tipoObrigacao.dia_recorrente}/${tipoObrigacao.mes_recorrente}`;
    } else {
      return `Dia ${tipoObrigacao.dia_recorrente}`;
    }
  };

  const gerarPDF = () => {
    if (obrigacoes.length === 0) {
      mostrarSnackbar("Não há dados para gerar o PDF", "warning");
      return;
    }

    const doc = new jsPDF();

    // Título do relatório
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Relatório - Obrigações Enviadas", 14, 22);

    // Data de geração
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const dataGeracao = new Date().toLocaleDateString("pt-BR");
    doc.text(`Gerado em: ${dataGeracao}`, 14, 30);

    // Filtros aplicados
    let filtrosTexto = "Filtros aplicados: ";
    if (filtros.dataInicio) {
      filtrosTexto += `Data início: ${filtros.dataInicio.toLocaleDateString("pt-BR")}`;
    }
    if (filtros.dataFim) {
      filtrosTexto += filtros.dataInicio ? ", " : "";
      filtrosTexto += `Data fim: ${filtros.dataFim.toLocaleDateString("pt-BR")}`;
    }
    if (filtros.cliente) {
      const clienteSelecionado = clientes.find(
        (c) => c.id === parseInt(filtros.cliente),
      );
      filtrosTexto += filtros.dataInicio || filtros.dataFim ? ", " : "";
      filtrosTexto += `Cliente: ${clienteSelecionado?.nome || "Não encontrado"}`;
    }
    if (!filtros.dataInicio && !filtros.dataFim && !filtros.cliente) {
      filtrosTexto += "Nenhum filtro aplicado";
    }

    doc.text(filtrosTexto, 14, 38);

    // Preparar dados da tabela
    const dadosTabela = obrigacoes.map((obrigacao) => [
      obrigacao.cliente?.nome || "-",
      obrigacao.tipo_obrigacao?.descricao || "-",
      getStatusLabel(obrigacao.status),
      formatarData(obrigacao.data_envio),
      obrigacao.enviado_por?.nome || "(usuário não identificado)",
      calcularPrazoLegal(obrigacao.tipo_obrigacao),
      obrigacao.comentarios || "-",
      obrigacao.setor?.descricao || "-",
    ]);

    // Cabeçalhos da tabela
    const cabecalhos = [
      "Cliente",
      "Tipo de obrigação",
      "Status",
      "Data de envio",
      "Enviado por",
      "Prazo legal",
      "Comentário",
      "Setor",
    ];

    // Gerar tabela
    autoTable(doc, {
      head: [cabecalhos],
      body: dadosTabela,
      startY: 45,
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 35 },
        2: { cellWidth: 20 },
        3: { cellWidth: 25 },
        4: { cellWidth: 20 },
        5: { cellWidth: 40 },
        6: { cellWidth: 25 },
      },
      margin: { left: 14, right: 14 },
    });

    // Rodapé com total de registros
    const finalY = doc.lastAutoTable.finalY || 50;
    doc.setFontSize(10);
    doc.text(`Total de registros: ${obrigacoes.length}`, 14, finalY + 10);

    // Salvar PDF
    const nomeArquivo = `relatorio-obrigacoes-enviadas-${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(nomeArquivo);

    mostrarSnackbar("PDF gerado com sucesso!", "success");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Box>
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleVoltar}
            sx={{ minWidth: "auto", px: 2 }}
          >
            ← Voltar
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#333" }}>
            Relatório - Obrigações enviadas
          </Typography>
        </Box>

        {/* <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Configure os filtros abaixo para gerar o relatório de obrigações
          enviadas aos clientes.
        </Typography> */}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Filtros
            </Typography>

            <Grid container spacing={3} alignItems="end">
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Vencimento inicial"
                  value={filtros.dataInicio}
                  onChange={(novaData) =>
                    handleFiltroChange("dataInicio", novaData)
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Vencimento final"
                  value={filtros.dataFim}
                  onChange={(novaData) =>
                    handleFiltroChange("dataFim", novaData)
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Cliente</InputLabel>
                  <Select
                    value={filtros.cliente}
                    label="Cliente"
                    onChange={(e) =>
                      handleFiltroChange("cliente", e.target.value)
                    }
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value="">Todos os clientes</MenuItem>
                    {clientes.map((cliente) => (
                      <MenuItem key={cliente.id} value={cliente.id}>
                        {cliente.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    onClick={handleGerarRelatorio}
                    sx={{ minWidth: 120 }}
                    disabled={loading}
                    size="small"
                  >
                    {loading ? "Carregando..." : "Gerar relatório"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      setFiltros({
                        dataInicio: null,
                        dataFim: null,
                        cliente: "",
                      })
                    }
                    size="small"
                  >
                    Limpar filtros
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={gerarPDF}
                    disabled={obrigacoes.length === 0}
                    size="small"
                    sx={{ color: "#d32f2f", borderColor: "#d32f2f" }}
                  >
                    📄 PDF
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabela de resultados */}
        <Card>
          <TableContainer component={Paper}>
            <Table sx={{ "& .MuiTableCell-root": { py: 1.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                    Cliente
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                    Tipo de obrigação
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                    Data de envio
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                    Prazo legal
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                    Comentário
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Setor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center", py: 2 }}>
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : obrigacoes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center", py: 2 }}>
                      Nenhuma obrigação enviada encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  obrigacoes.map((obrigacao) => (
                    <TableRow key={obrigacao.id} hover>
                      <TableCell sx={{ py: 1.5 }}>
                        {obrigacao.cliente?.nome || "-"}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {obrigacao.tipo_obrigacao?.descricao || "-"}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip
                          label={getStatusLabel(obrigacao.status)}
                          color={getStatusColor(obrigacao.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Box>
                          <Typography variant="body2">
                            {formatarData(obrigacao.data_envio)}
                          </Typography>
                          {obrigacao.enviado_por ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem" }}
                            >
                              por {obrigacao.enviado_por.nome}
                            </Typography>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.7rem", fontStyle: "italic" }}
                            >
                              (usuário não identificado)
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {calcularPrazoLegal(obrigacao.tipo_obrigacao)}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {obrigacao.comentarios || "-"}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {obrigacao.setor?.descricao || "-"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={paginacao.total}
              page={paginacao.page}
              onPageChange={handleChangePage}
              rowsPerPage={paginacao.limit}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Itens por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
              }
            />
          </TableContainer>
        </Card>

        {/* Snackbar para notificações */}
        <Snackbar
          open={snackbar.aberto}
          autoHideDuration={6000}
          onClose={fecharSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={fecharSnackbar}
            severity={snackbar.tipo}
            sx={{ width: "100%" }}
          >
            {snackbar.mensagem}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default ObrigacoesEnviadas;
